<template>
  <el-collapse v-model="collapseModel">
    <elemento-header
      :title="title"
      :name="name"
    >
      <input-suaje
        v-if="mostrarSuaje"
        v-model="localModel"
        :validate="validarCotizacionCampos"
        v-bind="componentsProps.InputSuaje"
        :mostrarEngranaje="false"
        :anchoImposicion="materialRef?.Ancho"
        :largoImposicion="materialRef?.Largo"
        @update:unidadImposicion="onUnidadImposicionChange"
      />

      <el-row
        v-if="mostrarSuaje"
        :gutter="8"
      >
        <custom-data
          :label="'Ancho mín. hoja'"
          :spanColumn="2"
          :data="fmt(anchoMinHoja)"
        />
        <custom-data
          :label="'Largo mín. hoja'"
          :spanColumn="2"
          :data="fmt(largoMinHoja)"
        />
        <custom-data
          :label="'Ancho item'"
          :spanColumn="2"
          :data="fmt(anchoMaterial)"
        />
        <custom-data
          :label="'Largo item'"
          :spanColumn="2"
          :data="fmt(largoMaterial)"
        />
      </el-row>
      <el-row
        v-if="mostrarSuaje"
        class="warn-row"
        :gutter="8"
      >
        <el-col :span="2"></el-col>
        <el-col :span="2"></el-col>
        <el-col :span="2">
          <div
            v-if="showWarnAncho"
            class="warn-text"
          >
            Ancho de item no llega al mínimo
          </div>
        </el-col>
        <el-col :span="2">
          <div
            v-if="showWarnLargo"
            class="warn-text"
          >
            Largo de item no llega al mínimo
          </div>
        </el-col>
      </el-row>
      <el-alert
        v-if="elementoTipo && !tieneProcesoValidaAncho"
        title="Configuración incompleta"
        type="warning"
        show-icon
        :closable="false"
      >
        El sistema no pudo determinar el proceso para validar el ancho de item.<br />
        Comuníquese con soporte.
      </el-alert>
      <input-procesos
        v-model="localModel.Secciones"
        :elemento-tipo="localModel.ElementoTipo"
      />
      <el-divider />
    </elemento-header>
  </el-collapse>
</template>

<script setup>
import {
  onBeforeMount,
  provide,
  computed,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useElemento } from '@/shared/composables/useElemento.js';
import { usePricesStore } from '@/views/prices/stores/prices_store.js';
import { useUnidadImposicionProvider } from '@/shared/composables/use_unidad_imposicion.js';
import CustomData from '@/shared/components/custom_data.vue';
import InputProcesos from '@/shared/components/procesos/input_procesos.vue';
import InputSuaje from '@/shared/components/suaje/input_suaje.vue';
// import InputTintas from '@/shared/components/tintas/input_tintas.vue'
import { useComponentsProps } from '@/shared/composables/use_components_props.js';
import { Modules } from '@/enum/modules';
import { ElementoBase } from '@/enum/elemento_base';
import { FACTOR } from '@/shared/composables/useUnitConversion';
import {
  aplicarMinimos,
  necesitaSincronizarMinimos,
} from '@/shared/composables/use_minimos_pliego.js';
const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: { type: Object, required: true },
  title: { type: String, required: true },
  name: { type: String, required: true },
});

const { localModel, collapseModel, updateModelo } = useElemento(props, emit);

// Proveer valores de imposición para componentes de proceso
const imposicionProvidedValue = computed(() => ({
  EtiquetasEje: localModel.value.EtiquetasEje || 0,
  EtiquetasDesarrollo: localModel.value.EtiquetasDesarrollo || 0,
}));

provide('currentElementoBase', ElementoBase.CAJA);
provide('currentElemento', localModel);
provide('imposicion', imposicionProvidedValue);
const { onUnidadImposicionChange } = useUnidadImposicionProvider(localModel);

const pricesStore = usePricesStore();
const { validarCotizacionCampos, procesoValidaAncho } = storeToRefs(pricesStore);
const { componentsProps } = useComponentsProps(ElementoBase.CAJA, Modules.COTIZACION);
const tieneProcesoValidaAncho = computed(() => !!procesoValidaAncho.value);

const elementoTipo = computed(
  () => localModel.value?.ElementoTipo?.CodigoElementoBase === ElementoBase.CAJA,
);

const materialImpresionSeleccionado = computed(() => {
  const secciones = localModel.value?.Secciones || [];

  for (const seccion of secciones) {
    for (const proc of seccion.ElementoProceso || []) {
      for (const cat of proc.CategoriasMaterial || []) {
        const validaAncho =
          cat.ValidaAnchoMaterial === true ||
          cat.MaterialCategoria?.ValidaAnchoMaterial === true ||
          (proc.Proceso?.ProcesoElementoTipoCategoriaMaterial || []).some(
            (rel) =>
              (rel?.CodigoCategoriaMaterial === cat.Codigo ||
                rel?.CategoriaMaterial?.Codigo === cat.Codigo) &&
              rel?.CategoriaMaterial?.ValidaAnchoMaterial === true,
          );

        if (validaAncho) {
          const mats = cat.ElementoMateriales || [];
          if (mats.length > 0) {
            return mats[0];
          }
        }
      }
    }
  }
  return null;
});

const materialRef = computed(() => materialImpresionSeleccionado.value || null);

const anchoMaterial = computed(() => {
  void localModel.value?.Secciones;
  return Number(
    materialImpresionSeleccionado.value?.Ancho ??
      materialImpresionSeleccionado.value?.Material?.Ancho ??
      0,
  );
});

const largoMaterial = computed(() => {
  void localModel.value?.Secciones;
  return Number(
    materialImpresionSeleccionado.value?.Largo ??
      materialImpresionSeleccionado.value?.Material?.Largo ??
      0,
  );
});

const mostrarSuaje = computed(() => {
  if (tieneProcesoValidaAncho.value) return true;

  const secciones = localModel.value?.Secciones || [];
  for (const seccion of secciones) {
    for (const proc of seccion.ElementoProceso || []) {
      for (const cat of proc.CategoriasMaterial || []) {
        const validaAncho =
          cat.ValidaAnchoMaterial === true ||
          cat.MaterialCategoria?.ValidaAnchoMaterial === true ||
          (proc.Proceso?.ProcesoElementoTipoCategoriaMaterial || []).some(
            (rel) =>
              (rel?.CodigoCategoriaMaterial === cat.Codigo ||
                rel?.CategoriaMaterial?.Codigo === cat.Codigo) &&
              rel?.CategoriaMaterial?.ValidaAnchoMaterial === true,
          );

        if (validaAncho) {
          return true;
        }
      }
    }
  }
  return false;
});

const anchoMinHoja = computed(() => {
  const e = localModel.value;
  const repAncho = Number(e?.EtiquetasEje ?? 0);
  const anchoProd = Number(e?.MedidaEje ?? 0);
  const lateralAncho = Number(e?.Laterales ?? 0);
  return anchoProd * repAncho + lateralAncho * 2;
});

const largoMinHoja = computed(() => {
  const e = localModel.value;
  const repLargo = Number(e?.EtiquetasDesarrollo ?? 0);
  const largoProd = Number(e?.MedidaDesarrollo ?? 0);
  const lateralLargo = Number(e?.MargenSuperior ?? 0);
  return largoProd * repLargo + lateralLargo * 2;
});

const unidad = computed(() =>
  (localModel.value?.UnidadMedida || 'mm').toLowerCase() === 'in' ? 'in' : 'mm',
);

const fmt = (mm) => {
  const n = Number(mm ?? 0);
  if (unidad.value === 'in') return `${(n / FACTOR).toFixed(4)} in`;
  return `${n.toFixed(2)} mm`;
};

const showWarnAncho = computed(() => {
  const am = Number(anchoMaterial.value ?? 0);
  const req = Number(anchoMinHoja.value ?? 0);
  return am > 0 && req > 0 && am < req;
});

const showWarnLargo = computed(() => {
  const lm = Number(largoMaterial.value ?? 0);
  const req = Number(largoMinHoja.value ?? 0);
  return lm > 0 && req > 0 && lm < req;
});

onBeforeMount(() => {
  updateModelo({
    ...localModel.value,
    EtiquetasEje: localModel.value.EtiquetasEje || 1,
    EtiquetasDesarrollo: localModel.value.EtiquetasDesarrollo || 1,
  });
});

watch(
  [
    anchoMinHoja,
    largoMinHoja,
    () => localModel.value?.AnchoMinimoHojaCalculado,
    () => localModel.value?.LargoMinimoHojaCalculado,
    () => localModel.value?.AnchoMinimoMaterial,
    () => localModel.value?.LargoMinimoMaterial,
  ],
  () => {
    if (!localModel.value) return;
    const minimos = { ancho: anchoMinHoja.value, largo: largoMinHoja.value };
    if (!necesitaSincronizarMinimos(localModel.value, minimos)) return;

    aplicarMinimos(localModel.value, minimos);
    updateModelo(localModel.value);
  },
  { immediate: true },
);
</script>

<style scoped>
.detail-container-printed {
  padding: 0;
}
.custom-label {
  font-weight: 700;
  color: #111;
}

.colors-button {
  width: 100%;
}
.warn-row {
  margin-top: -6px;
}
.warn-text {
  color: #d93025;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.1;
}
</style>
